import getPageTypeFromPath from 'components/CreatorStoreFront/helpers/getPageTypeFromPath';
import getSiteIdFromLinkSynergy from 'services/api/creatorStoreFront/getSiteIdFromLinkSynergy';
import { CSF_PAGE_TYPES } from 'constants/actionTypes/creatorStoreFront';
import Empty from 'constants/empty';
import LOCAL_STORAGE from 'utils/localStorage/Constants';
import Storage from 'utils/localStorage/Storage';
import Tables from 'utils/indexedDB/Tables';
import UUIDv4 from 'utils/UUID';

// ──────────────────────────────────────────────────────────────────────────────
// Private u1-construction primitives — duplicated from `csfAttribution.js`.
//
// These are pure utility functions (no storage / Redux / network side-effects).
// We deliberately duplicate them here instead of importing from the legacy
// module to maintain strict isolation between Dynamic Commissioning and the
// legacy CSF attribution flow. Keeping these stable string-builders local
// avoids coupling new Dynamic Commissioning logic to the legacy attribution
// module.
//
// These are stable string-builders. The DC copy may **intentionally diverge**
// from legacy where DC needs corrected behavior — the legacy copy is left
// untouched per the DC ↔ Legacy isolation rule (DC is a strangler-fig
// replacement, not a refactor of legacy). Per-function JSDoc records any
// intentional divergence and its rationale. Removal of this duplication
// happens when legacy CSF attribution is decommissioned — not inline with
// feature work.
// ──────────────────────────────────────────────────────────────────────────────

const U1_COLLECTION_DETAILS_PAGE_NAME = 'collectiondetail';
const U1_POST_DETAILS_PAGE_NAME = 'postdetail';

/* eslint-disable camelcase */
function constructU1Param({
    host_type,
    traffic_source,
    device,
    page_type,
    identifier_location = '',
    merchant_tag,
    storefront_tag
}) {
    const prefix = [host_type, traffic_source, device, page_type, identifier_location].join('-');

    return [prefix, merchant_tag, storefront_tag].filter(Boolean).join('_');
}
/* eslint-enable camelcase */

function getPageTypeForAttribution(section, identifier) {
    const CXXC = 'cxxc';
    const PXXC = 'pxxc';
    const XXXC = 'xxxc';

    if (identifier) {
        if (section === CSF_PAGE_TYPES.POSTS) {
            return PXXC;
        } else if (section === CSF_PAGE_TYPES.COLLECTIONS) {
            return CXXC;
        }
    }

    const pageTypeMap = {
        [CSF_PAGE_TYPES.FEATURED]: XXXC,
        [CSF_PAGE_TYPES.POSTS]: XXXC,
        [CSF_PAGE_TYPES.COLLECTIONS]: XXXC,
        [CSF_PAGE_TYPES.PRODUCTS]: XXXC,
        '': XXXC
    };

    return pageTypeMap[section] || XXXC;
}

function generatePageIdentifierLocation(subAffiliateId, section, identifier) {
    if (identifier) {
        if (section === CSF_PAGE_TYPES.COLLECTIONS) {
            return `${identifier}_${U1_COLLECTION_DETAILS_PAGE_NAME}`;
        } else if (section === CSF_PAGE_TYPES.POSTS) {
            return `${identifier}_${U1_POST_DETAILS_PAGE_NAME}`;
        }
    }

    const location = !section || section === '' || section === CSF_PAGE_TYPES.FEATURED ? 'main' : section;

    return `${subAffiliateId}_${location}`;
}

function getSocialSourceFromReferrer(referrer) {
    const referrerLower = referrer.toLowerCase();

    if (referrerLower.includes('instagram.com')) {
        return 'i';
    }

    if (referrerLower.includes('tiktok.com')) {
        return 't';
    }

    if (referrerLower.includes('youtube.com') || referrerLower.includes('youtu.be')) {
        return 'y';
    }

    return 'o';
}

function getSocialSourceFromUserAgent(userAgent) {
    const userAgentLower = userAgent.toLowerCase();

    if (userAgentLower.includes('instagram') || userAgentLower.includes('igwebview') || userAgentLower.includes('igloader')) {
        return 'i';
    }

    if (userAgentLower.includes('tiktok') || userAgentLower.includes('musically') || userAgentLower.includes('bytedance')) {
        return 't';
    }

    if (userAgentLower.includes('youtube')) {
        return 'y';
    }

    return 'o';
}

/**
 * Originally duplicated from `csfAttribution.js`'s `getDetailedSocialSource`,
 * but **intentionally diverged**: this DC copy fixes a latent bug where the
 * legacy `||` chain short-circuits on the referrer's truthy `'o'` and never
 * reaches the UA branch or the `'so'` fallback. The legacy copy is left
 * untouched per the DC ↔ Legacy isolation rule — DC is a strangler-fig
 * replacement, not a refactor of legacy. The legacy bug will disappear when
 * legacy CSF attribution is removed; until then, `traffic_source` (and thus
 * `u1`) for `om_mmc=social-…` URLs in social-app WebViews will differ between
 * legacy and DC pings — accepted as transitional cost of running both flows
 * in parallel behind the `csf.dynamicCommissioning.isEnabled` flag.
 */
function getDetailedSocialSource(omMmc, referrer, userAgent) {
    const omMmcLower = omMmc.toLowerCase();

    if (omMmcLower.includes('instagram') || omMmcLower.includes('ig')) {
        return 'i';
    }

    if (omMmcLower.includes('tiktok') || omMmcLower.includes('tt')) {
        return 't';
    }

    if (omMmcLower.includes('youtube') || omMmcLower.includes('yt')) {
        return 'y';
    }

    // Both helpers always return a truthy string (`'i'|'t'|'y'|'o'`), so a
    // plain `||` chain would short-circuit on the referrer's `'o'` and never
    // reach the UA branch or the `'so'` fallback. Step through the layers
    // explicitly with `!== 'o'`, mirroring the same pattern used below in
    // `getTrafficSource`.
    const socialSourceFromReferrer = getSocialSourceFromReferrer(referrer);

    if (socialSourceFromReferrer !== 'o') {
        return socialSourceFromReferrer;
    }

    const socialSourceFromUserAgent = getSocialSourceFromUserAgent(userAgent);

    if (socialSourceFromUserAgent !== 'o') {
        return socialSourceFromUserAgent;
    }

    return 'so';
}

function getSourceFromReferrer(referrer) {
    if (!referrer) {
        return 'o';
    }

    const referrerLower = referrer.toLowerCase();

    if (
        referrerLower.includes('mail.') ||
        referrerLower.includes('gmail.') ||
        referrerLower.includes('outlook.') ||
        referrerLower.includes('yahoo.')
    ) {
        return 'e';
    }

    return 'o';
}

function isSearchEngineReferrer(referrer) {
    if (!referrer) {
        return false;
    }

    const searchEngines = ['google.', 'bing.', 'yahoo.', 'duckduckgo.', 'baidu.', 'yandex.', 'ask.', 'aol.'];
    const referrerLower = referrer.toLowerCase();

    return searchEngines.some(engine => referrerLower.includes(engine));
}

function getTrafficSource() {
    const referrer = document.referrer || '';
    const userAgent = navigator.userAgent || '';

    const urlParams = new URLSearchParams(window.location.search);
    const omMmc = urlParams.get('om_mmc');

    if (omMmc) {
        const omMmcParts = omMmc.toLowerCase().split('-');

        if (omMmcParts.length > 0) {
            const source = omMmcParts[0];
            const omMmcSourceMap = {
                social: getDetailedSocialSource(omMmc, referrer, userAgent),
                email: 'e',
                sms: 's',
                paid: 'p',
                display: 'd',
                affiliate: 'a'
            };

            if (omMmcSourceMap[source]) {
                return omMmcSourceMap[source];
            }
        }
    }

    const socialSource = getSocialSourceFromReferrer(referrer);

    if (socialSource !== 'o') {
        return socialSource;
    }

    const userAgentSource = getSocialSourceFromUserAgent(userAgent);

    if (userAgentSource !== 'o') {
        return userAgentSource;
    }

    const referrerSource = getSourceFromReferrer(referrer);

    if (referrerSource !== 'o') {
        return referrerSource;
    }

    if (isSearchEngineReferrer(referrer)) {
        return 's';
    }

    return 'o';
}

function getDeviceOS() {
    const userAgent = navigator.userAgent || '';

    if (userAgent.includes('iPhone')) {
        return 'i';
    }

    if (userAgent.includes('Mac OS')) {
        return 'm';
    }

    if (userAgent.includes('Windows')) {
        return 'w';
    }

    if (userAgent.includes('Android')) {
        return 'a';
    }

    return 'm';
}

/** TTL for the standalone CSF dynamic commissioning data record (90 days, expressed in milliseconds — Storage.DAYS is one day in ms). */
const CSF_DYNAMIC_COMMISSIONING_DATA_STORAGE_TTL_MS = Storage.DAYS * 90;

/**
 * Persist the dynamic-commissioning `u5` (creator tier) value alongside the
 * `subAffiliateId` it was captured for. Stored in a dedicated localStorage
 * key (`CSF_DYNAMIC_COMMISSIONING_DATA`) so the new dynamic-commissioning
 * flow does not share state with the legacy `csfAttributionData` blob. The
 * record is shaped as a container envelope so future DC-only fields (e.g.
 * `u1`, `siteId`) can be added without splitting them across separate
 * localStorage keys; new fields will land via parallel setters that
 * read-modify-write the same record.
 *
 * Cross-creator leakage protection layers:
 *  - **Write side**: this function is called from
 *    `captureCsfDynamicCommissioningU5FromProfile` on every creator change,
 *    so the record is overwritten with the new creator's `u5`/`subAffiliateId`
 *    whenever a fresh Profile API response arrives. On Profile API failure
 *    the caller invokes `clearCsfDynamicCommissioningU5`. When the feature
 *    flag flips off, the next capture clears the record.
 *  - **Read side**: {@link getCsfDynamicCommissioningU5} accepts an
 *    `expectedSubAffiliateId` and returns `null` on mismatch. Used by
 *    `initializeDcLinkSynergyAffiliate` to defend against the
 *    deferred-execution race on rapid SPA creator switches (A→B→C where C's
 *    capture overwrites storage before B's deferred ping macrotask fires).
 *
 * @param {{ u5: string|null, subAffiliateId: string|null }} params
 */
export function setCsfDynamicCommissioningU5({ u5 = null, subAffiliateId = null } = {}) {
    // SSR guard — `Storage.local` in node renders is built from
    // `errorMethods(...)` (functions that throw), so the typeof check
    // alone is not enough: node-render paths can have `global.window`
    // defined while `Storage.local` is still the throw-shim.
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return;
    }

    try {
        // A record without a subAffiliateId is treated as malformed and refused —
        // without it the read-side mismatch guard cannot validate the record
        // against the current creator, defeating the cross-creator leakage
        // protection. Drop any prior record in this case so we don't silently
        // keep a stale tier around.
        if (!subAffiliateId) {
            Storage.local.removeItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA);

            return;
        }

        Storage.local.setItem(
            LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA,
            { u5: u5 ?? null, subAffiliateId, updatedAt: Date.now() },
            CSF_DYNAMIC_COMMISSIONING_DATA_STORAGE_TTL_MS
        );
    } catch (err) {
        Sephora.logger.error('CSF dynamic commissioning: failed to persist u5', err);
    }
}

/**
 * Reads the persisted dynamic-commissioning `u5`. Returns `null` when no
 * record exists, the record is malformed, storage is unavailable, or — when
 * `expectedSubAffiliateId` is supplied — the persisted `subAffiliateId` does
 * not match the expected one.
 *
 * The optional mismatch guard exists so consumers that build a payload from
 * a single creator-context snapshot (e.g. `initializeDcLinkSynergyAffiliate`
 * fired via `deferTaskExecution`) can avoid attaching another creator's u5
 * if a later creator switch overwrote storage between the schedule and the
 * deferred execution. When `expectedSubAffiliateId` is omitted, behavior is
 * the legacy `return record.u5 if present` — used by `logCsfPageView` where
 * the call is sync with the navigation handler and no race window exists.
 *
 * @param {{ expectedSubAffiliateId?: string|null }} [options]
 * @returns {string|null}
 */
export function getCsfDynamicCommissioningU5({ expectedSubAffiliateId = null } = {}) {
    // SSR guard — see `setCsfDynamicCommissioningU5` for the rationale.
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return null;
    }

    try {
        const record = Storage.local.getItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA);

        if (!record || typeof record.u5 !== 'string' || record.u5.length === 0) {
            return null;
        }

        // Mismatch guard: when caller supplied an expected subAffiliateId and
        // the persisted record was captured for a different creator, omit u5
        // so the caller's payload is built strictly from one creator context.
        if (expectedSubAffiliateId && record.subAffiliateId !== expectedSubAffiliateId) {
            return null;
        }

        return record.u5;
    } catch {
        return null;
    }
}

/**
 * Clears the persisted dynamic-commissioning data record (currently holding
 * `u5` + `subAffiliateId`; future DC-only fields like `u1`/`siteId` will share the
 * same record and be cleared together).
 */
export function clearCsfDynamicCommissioningU5() {
    // SSR guard — see `setCsfDynamicCommissioningU5` for the rationale.
    if (typeof window === 'undefined' || Sephora.isNodeRender || !Storage?.local) {
        return;
    }

    try {
        Storage.local.removeItem(LOCAL_STORAGE.CSF_DYNAMIC_COMMISSIONING_DATA);
    } catch {
        // best-effort; nothing else to do.
    }
}

/**
 * Feature-flag check for CSF dynamic commissioning.
 *
 * @returns {boolean}
 */
export function isDynamicCommissioningEnabled() {
    return Sephora.configurationSettings?.csf?.dynamicCommissioning?.isEnabled === true;
}

/**
 * Captures the dynamic-commissioning `u5` directly from a freshly-loaded
 * creator Profile API response and persists it under `CSF_DYNAMIC_COMMISSIONING_DATA`. This is the
 * single dynamic-commissioning capture point — it is fully self-contained and
 * does not read from or mutate the legacy `csfAttributionData` blob.
 *
 * Behavior:
 * - Feature flag off → clears any prior record and returns.
 * - Profile has a non-empty `u5` → persists it with the profile's `subAffiliateId`.
 * - Profile has no `u5` (or empty/whitespace) → persists `null` under the
 *   current `subAffiliateId` (graceful fallback per CSF-1725 AC).
 * - Missing `subAffiliateId` → strict guard in `setCsfDynamicCommissioningU5`
 *   removes the prior record (cannot validate on read otherwise).
 *
 * @param {object} [creatorProfile] Fresh response from the creator Profile API.
 */
export function captureCsfDynamicCommissioningU5FromProfile(creatorProfile) {
    if (!isDynamicCommissioningEnabled()) {
        clearCsfDynamicCommissioningU5();

        return;
    }

    const attributionDetails = creatorProfile?.attributionDetails || Empty.Object;
    const { subAffiliateId, u5 } = attributionDetails;
    const trimmedU5 = typeof u5 === 'string' ? u5.trim() : null;
    const u5Value = trimmedU5 && trimmedU5.length > 0 ? trimmedU5 : null;

    setCsfDynamicCommissioningU5({ u5: u5Value, subAffiliateId: subAffiliateId ?? null });
}

export const CSF_JOURNEYS_KEY = 'csf_journeys';

/** Journey TTL — 15 days from entry timestamp (dynamic commissioning schema). */
export const CSF_JOURNEY_TTL_MS = 15 * 24 * 60 * 60 * 1000;

/**
 * Embedded fallback for CSF Dynamic Commissioning rules (RQL AST v2). Used when
 * `Sephora.configurationSettings.csf.dynamicCommissioning.rules` is missing or
 * cannot be parsed as JSON. Source: CSF-1739 attachment `rql-rules-fallback-v2.json`.
 */
const DEFAULT_DC_RULES = {
    'version': '2.2.0',
    'rules': [
        {
            'rule_id': 'AFFILIATE_EXTERNAL_BLOCKED',
            'query': [
                'REQUIRE',
                ['NOT', ['CONTAINS', 'events', 'om_mmc', 'motom']],
                [
                    'FALLBACK',
                    ['WHEN_ENTRY_FROM', 'events', 'rakuten\\.com|linksynergy\\.com'],
                    ['CONTAINS', 'events', 'om_mmc', '^aff$']
                ]
            ],
            'target': null
        },
        {
            'rule_id': 'SOCIAL_CREATOR_POSTED_FIRST_TOUCH',
            'query': [
                'MIN',
                [
                    'WITH_CREATOR',
                    [
                        'REQUIRE',
                        ['WHEN_ENTRY_FROM', 'events', 'instagram\\.com|tiktok\\.com|facebook\\.com'],
                        ['NOT', ['CONTAINS', 'events', 'om_mmc', '.+']]
                    ]
                ]
            ],
            'target': 'creator_id'
        },
        {
            'rule_id': 'DEFAULT_FALLBACK_NO_MATCH',
            'query': [
                'REQUIRE',
                ['ENTRY', ['ALL', 'events']],
                ['FIRST', ['WITH_CREATOR', ['ALL', 'events']]]
            ],
            'target': 'creator_id'
        }
    ]
};

/**
 * Returns CSF Dynamic Commissioning rules from ConfigHub
 * (`Sephora.configurationSettings.csf.dynamicCommissioning.rules`, a JSON string),
 * falling back to the embedded {@link DEFAULT_DC_RULES} when the value is
 * missing or fails to parse.
 *
 * @returns {{ version: string, rules: Array<object> }}
 */
export function getDynamicCommissioningRules() {
    const raw = Sephora?.configurationSettings?.csf?.dynamicCommissioning?.rules;

    try {
        const parsed = JSON.parse(raw);

        if (
            parsed &&
            typeof parsed === 'object' &&
            typeof parsed.version === 'string' &&
            Array.isArray(parsed.rules)
        ) {
            return parsed;
        }
    } catch {
        // fall through to defaults
    }

    return JSON.parse(JSON.stringify(DEFAULT_DC_RULES));
}

/** Canonical `event_type` strings for CSF dynamic commissioning journeys. */
export const CSF_EVENT_TYPES = {
    ENTRY: 'entry',
    INTERNAL_SEARCH: 'internal_search',
    PAGE_VIEW: 'page_view',
    ADD_TO_CART: 'add_to_cart',
    QUICKLOOK_ATB: 'quicklook_atb',
    EXIT: 'exit'
};

/**
 * Builds `u1` / site / mid / creator ids for dynamic commissioning from the creator profile and route,
 * using this module's local Dynamic Commissioning string-building helpers.
 * These helpers were derived from legacy CSF attribution logic but are intentionally
 * isolated in this module and may diverge from legacy fresh-write behavior in some cases.
 * Reads only the fresh Profile API response — does NOT read `CSF_ATTRIBUTION_DATA`,
 * so the returned `u1` may differ both from the reused-within-5-days U1 stored there
 * and from a legacy `captureAttributionData` fresh write when DC behavior intentionally diverges.
 *
 * @param {{ creatorProfile?: object, section?: string, identifier?: string|null }} params
 * @returns {{ u1: string, siteId: string|null, mid: string|number|null, subAffiliateId: string }|null}
 */
export function buildCommissioningAttributionFieldsFromProfile({ creatorProfile, section, identifier = null } = {}) {
    if (!creatorProfile) {
        return null;
    }

    const attributionDetails = creatorProfile?.attributionDetails || Empty.Object;
    const {
        linkshareSiteId, merchantTag, mid, storefrontTag, subAffiliateId
    } = attributionDetails;

    if (!subAffiliateId) {
        return null;
    }

    const pageType = getPageTypeForAttribution(section, identifier);
    const pageIdentifierLocation = generatePageIdentifierLocation(subAffiliateId, section, identifier);

    /* eslint-disable camelcase */
    const u1 = constructU1Param({
        host_type: 'b',
        traffic_source: getTrafficSource(),
        device: getDeviceOS(),
        page_type: pageType,
        identifier_location: pageIdentifierLocation,
        merchant_tag: merchantTag,
        storefront_tag: storefrontTag
    });
    /* eslint-enable camelcase */

    return {
        u1,
        siteId: linkshareSiteId || null,
        mid: mid ?? null,
        subAffiliateId
    };
}

/**
 * Dynamic Commissioning thin wrapper around the shared low-level
 * `getSiteIdFromLinkSynergy` HTTP service. Parallel to the legacy
 * `fetchSiteIdFromLinkSynergy` in `csfAttribution.js` — duplicated
 * intentionally to keep DC and legacy fully decoupled (the DC orchestrator
 * never reaches into legacy modules to make its adapter call).
 *
 * Accepts `u5` as a first-class parameter (unlike the legacy wrapper, which
 * never carries DC fields). The shared low-level service omits `u5` from
 * the payload when it is `undefined`/empty — graceful fallback.
 *
 * @param {string} linkshareSiteId
 * @param {string|number} mid
 * @param {string} murl
 * @param {string} u1
 * @param {string} [u5]
 * @returns {Promise<{data: object, headers: object}>} Low-level API result object
 */
export async function fetchDcSiteIdFromLinkSynergy(linkshareSiteId, mid, murl, u1, u5) {
    try {
        const result = await getSiteIdFromLinkSynergy(linkshareSiteId, mid, murl, u1, u5);

        Sephora.logger.verbose('CSF dynamic commissioning: LinkSynergy site ID fetched', result.data);

        return result;
    } catch (error) {
        Sephora.logger.error('CSF dynamic commissioning: error fetching site ID from LinkSynergy', error);
        throw error;
    }
}

/**
 * Dynamic Commissioning LinkSynergy adapter ping. Parallel to the legacy
 * `initializeLinkSynergyAffiliate` in `csfAttribution.js`, but builds its
 * payload strictly from Dynamic Commissioning / Profile-API-owned data —
 * never from the legacy `CSF_ATTRIBUTION_DATA` blob.
 *
 * Payload fields:
 *  - `linkshareSiteId`, `mid` — from the fresh creator Profile API response.
 *  - `u1` — built fresh via {@link buildCommissioningAttributionFieldsFromProfile}.
 *  - `murl` — current page URL.
 *  - `u5` — read from the standalone `CSF_DYNAMIC_COMMISSIONING_DATA` record
 *    (single source of truth per CSF-1725 — "persist u5 for reuse across
 *    tracking events"). Read with `expectedSubAffiliateId` set to the
 *    profile's `subAffiliateId`: the getter returns `null` when the
 *    persisted record was captured for a different creator, defending
 *    against the deferred-execution race on rapid A→B→C SPA switches where
 *    C's capture may overwrite storage between B's `deferTaskExecution`
 *    schedule and B's macrotask actually firing. On mismatch u5 is omitted
 *    from the payload rather than risk attaching another creator's tier to
 *    this creator's u1/mid/siteId.
 *
 * Calls the DC-specific {@link fetchDcSiteIdFromLinkSynergy} wrapper (which
 * in turn calls the shared low-level HTTP service). No-ops when the DC
 * feature flag is off, when `creatorProfile` is missing, or when the profile
 * lacks the required `linkshareSiteId`/`mid` fields.
 *
 * @param {{ creatorProfile?: object, section?: string, identifier?: string|null }} params
 * @returns {Promise<void>}
 */
export async function initializeDcLinkSynergyAffiliate({ creatorProfile, section, identifier = null } = {}) {
    if (typeof window === 'undefined' || Sephora.isNodeRender) {
        return;
    }

    if (!isDynamicCommissioningEnabled()) {
        return;
    }

    const attribution = buildCommissioningAttributionFieldsFromProfile({ creatorProfile, section, identifier });

    if (!attribution) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping LinkSynergy ping — no commissioning attribution');

        return;
    }

    const { u1, siteId: linkshareSiteId, mid } = attribution;

    if (!linkshareSiteId || !mid || !u1) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping LinkSynergy ping — missing required fields', {
            linkshareSiteId,
            mid,
            hasU1: Boolean(u1)
        });

        return;
    }

    // u5 is optional — graceful fallback when the Profile API did not return
    // a tier (the low-level service omits it from the payload when undefined).
    // Pass `expectedSubAffiliateId` so the getter returns `null` if storage
    // was overwritten by a later creator's capture between this ping's
    // `deferTaskExecution` schedule and its macrotask firing.
    const u5 = getCsfDynamicCommissioningU5({
        expectedSubAffiliateId: creatorProfile?.attributionDetails?.subAffiliateId
    }) ?? undefined;

    try {
        const result = await fetchDcSiteIdFromLinkSynergy(linkshareSiteId, mid, window.location.href, u1, u5);

        Sephora.logger.verbose('CSF dynamic commissioning: LinkSynergy ping fired', { siteID: result?.data?.siteID, u1, u5 });
    } catch (err) {
        // Ping failures must not break the surrounding flow — already
        // logged inside `fetchDcSiteIdFromLinkSynergy`; swallow here.
        Sephora.logger.verbose('CSF dynamic commissioning: LinkSynergy ping failed (handled)', err);
    }
}

const csfMenuTabOrder = [CSF_PAGE_TYPES.FEATURED, CSF_PAGE_TYPES.PRODUCTS, CSF_PAGE_TYPES.COLLECTIONS, CSF_PAGE_TYPES.POSTS];

/** Same filter/map rules as `getFormattedMenuItems` in `withCreatorStoreFrontProps` (post-profile redirect only). */
function getFormattedMenuItemsForContentRedirect(pageType, textResources = {}, contentSummary = {}) {
    return csfMenuTabOrder
        .filter(item => {
            const { featuredCount = 0, productsCount = 0, collectionsCount = 0, postsCount = 0 } = contentSummary;

            switch (item) {
                case CSF_PAGE_TYPES.FEATURED:
                    return featuredCount > 0;
                case CSF_PAGE_TYPES.PRODUCTS:
                    return productsCount > 0;
                case CSF_PAGE_TYPES.COLLECTIONS:
                    return collectionsCount > 0;
                case CSF_PAGE_TYPES.POSTS:
                    return postsCount > 0;
                default:
                    return true;
            }
        })
        .map(item => ({
            title: textResources[item] || item,
            link: item === CSF_PAGE_TYPES.FEATURED ? '/' : `/${item.toLowerCase().replace(/\s+/g, '-')}`,
            isActive: item === pageType
        }));
}

/**
 * Prefer `profilePayload` for content counts: Redux props may not have updated in the same tick after profile init.
 *
 * @param {{ profilePayload?: object, props: object, pathname: string }} params
 * @returns {{ contentSummary: object, formattedMenuItemsForRedirect: Array<{ title: string, link: string, isActive: boolean }> }}
 */
export function getContentTabContextAfterProfileLoad({ profilePayload, props, pathname }) {
    const { creatorProfileData, creatorStoreFrontData, textResources } = props;
    const contentSummary = profilePayload?.creatorProfile?.contentSummary ?? creatorProfileData?.creatorProfile?.contentSummary ?? Empty.Object;
    const pageTypeForMenu = getPageTypeFromPath(pathname) || creatorStoreFrontData?.pageType || CSF_PAGE_TYPES.FEATURED;
    const formattedMenuItemsForRedirect = getFormattedMenuItemsForContentRedirect(pageTypeForMenu, textResources, contentSummary);

    return { contentSummary, formattedMenuItemsForRedirect };
}

function isInternalSephoraReferrer(referrer) {
    if (!referrer) {
        return false;
    }

    try {
        const { hostname } = new URL(referrer);

        return /(^|\.)sephora\./i.test(hostname.toLowerCase());
    } catch {
        return false;
    }
}

function getOmMmcFromUrl() {
    if (typeof window === 'undefined') {
        return null;
    }

    const raw = new URLSearchParams(window.location.search).get('om_mmc');

    if (!raw || !String(raw).trim()) {
        return null;
    }

    return String(raw).trim();
}

/* eslint-disable camelcase */
function buildCsfEvent(overrides = {}) {
    return {
        id: overrides.id || UUIDv4(),
        timestamp: typeof overrides.timestamp === 'number' ? overrides.timestamp : Date.now(),
        event_type: overrides.event_type || null,
        source_url: overrides.source_url ?? null,
        destination_url: overrides.destination_url ?? null,
        creator_id: overrides.creator_id ?? null,
        u1: overrides.u1 ?? null,
        u5: overrides.u5 ?? null,
        siteId: overrides.siteId ?? null,
        navigation_type: overrides.navigation_type ?? null,
        om_mmc: overrides.om_mmc ?? null,
        product_id: overrides.product_id ?? null,
        motom_product_id: overrides.motom_product_id ?? null,
        search_term: overrides.search_term ?? null
    };
}

function buildCsfJourney(entryEvent) {
    const entryTimestamp = entryEvent?.timestamp ?? Date.now();

    return {
        journey_id: UUIDv4(),
        entry_timestamp: entryTimestamp,
        expires_at: entryTimestamp + CSF_JOURNEY_TTL_MS,
        events: entryEvent ? [entryEvent] : []
    };
}
/* eslint-enable camelcase */

// ──────────────────────────────────────────────────────────────────────────────
// Serialized writer for `csf_journeys`.
//
// Journey writes are read-modify-write. Multiple rapid SPA navigations
// (e.g. quick tab taps) issue overlapping writes; without serialization the
// later setItem clobbers events appended by the earlier one. Each task is
// chained through a single module-level promise so writes are strictly
// ordered. A task's failure does NOT break the chain for subsequent tasks.
// ──────────────────────────────────────────────────────────────────────────────

let journeysWriteQueue = Promise.resolve();

/**
 * Enqueue an async read-modify-write task against `csf_journeys`. Tasks run
 * strictly in submission order; one task's rejection is isolated from the
 * tail of the queue.
 *
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function enqueueJourneysWrite(task) {
    const next = journeysWriteQueue.then(task, task);

    journeysWriteQueue = next.catch(() => {});

    return next;
}

function isMissingRequiredValue(value) {
    return value === null || value === undefined || value === '';
}

function getMissingRequiredEntryFields({
    destinationUrl,
    creatorId,
    u1,
    siteId,
    productId = null,
    motomProductId = null,
    includeDestinationUrl = true,
    includeAtbFields = false
}) {
    const missing = [];

    if (includeDestinationUrl && !destinationUrl) {
        missing.push('destination_url');
    }

    if (isMissingRequiredValue(creatorId)) {
        missing.push('creator_id');
    }

    if (isMissingRequiredValue(u1)) {
        missing.push('u1');
    }

    if (isMissingRequiredValue(siteId)) {
        missing.push('siteId');
    }

    if (includeAtbFields) {
        if (isMissingRequiredValue(productId)) {
            missing.push('product_id');
        }

        if (isMissingRequiredValue(motomProductId)) {
            missing.push('motom_product_id');
        }
    }

    return missing;
}

/**
 * Persists a CSF entry journey when dynamic commissioning is enabled.
 * No-ops when the feature flag is off or required storage APIs are unavailable.
 *
 * @param {{ destinationUrl?: string, creatorId?: string|null, u5?: string|null, u1?: string|null, siteId?: string|null, attributionSubAffiliateId?: string|null }} [params]
 * @returns {Promise<void>}
 */
export async function logCsfEntryInComponent({
    destinationUrl,
    creatorId,
    u5 = null,
    u1 = null,
    siteId = null,
    attributionSubAffiliateId = null
} = {}) {
    if (!isDynamicCommissioningEnabled()) {
        return;
    }

    // SSR guard — `Storage.db` in node renders is built from `errorMethods(...)`
    // (functions that throw), so the typeof check below is not enough.
    if (Sephora.isNodeRender) {
        return;
    }

    if (typeof Storage.db?.getItem !== 'function' || typeof Storage.db?.setItem !== 'function') {
        return;
    }

    const referrer = (typeof document !== 'undefined' && document.referrer) || '';

    if (referrer && isInternalSephoraReferrer(referrer)) {
        return;
    }

    const sourceUrl = referrer ? referrer : null;
    const omMmc = getOmMmcFromUrl();

    const resolvedCreatorId = creatorId || attributionSubAffiliateId || null;

    const missing = getMissingRequiredEntryFields({
        destinationUrl,
        creatorId: resolvedCreatorId,
        u1,
        siteId,
        includeDestinationUrl: true,
        includeAtbFields: false
    });

    if (missing.length) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping entry, missing fields', missing);

        return;
    }

    /* eslint-disable camelcase */
    const event = buildCsfEvent({
        event_type: CSF_EVENT_TYPES.ENTRY,
        source_url: sourceUrl,
        destination_url: destinationUrl,
        creator_id: resolvedCreatorId,
        u1,
        u5,
        siteId,
        om_mmc: omMmc
    });
    /* eslint-enable camelcase */

    await enqueueJourneysWrite(async () => {
        try {
            const existing = await Storage.db.getItem(CSF_JOURNEYS_KEY, true, false, false, Tables.CsfAttribution);
            const journeys = Array.isArray(existing?.journeys) ? existing.journeys : [];

            await Storage.db.setItem(CSF_JOURNEYS_KEY, { journeys: [...journeys, buildCsfJourney(event)] }, null, Tables.CsfAttribution);
        } catch (err) {
            Sephora.logger.error('CSF dynamic commissioning: failed to log entry event', err);
        }
    });
}

function journeyContainsEntryEvent(journey) {
    const events = Array.isArray(journey?.events) ? journey.events : [];

    return events.some(e => e?.event_type === CSF_EVENT_TYPES.ENTRY);
}

/**
 * Persists CSF add-to-basket events (tile and quicklook) when dynamic commissioning is enabled.
 * Appends only to an existing journey that already has an **`entry`** event; does not create
 * journeys (that is reserved for `logCsfEntryInComponent`).
 *
 * @param {{
 *   eventType?: string,
 *   creatorId?: string|null,
 *   u1?: string|null,
 *   u5?: string|null,
 *   siteId?: string|null,
 *   productId?: string|null,
 *   motomProductId?: string|null
 * }} [params]
 * @returns {Promise<void>}
 */
export async function logCsfAtbEvent({
    eventType = CSF_EVENT_TYPES.ADD_TO_CART,
    creatorId = null,
    u1 = null,
    u5 = null,
    siteId = null,
    productId = null,
    motomProductId = null
} = {}) {
    if (!isDynamicCommissioningEnabled()) {
        return;
    }

    if (typeof Storage.db?.getItem !== 'function' || typeof Storage.db?.setItem !== 'function') {
        return;
    }

    const missing = getMissingRequiredEntryFields({
        creatorId,
        u1,
        siteId,
        productId,
        motomProductId,
        includeDestinationUrl: false,
        includeAtbFields: true
    });

    if (missing.length) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping ATB event, missing fields', missing);

        return;
    }

    const resolvedEventType = eventType === CSF_EVENT_TYPES.QUICKLOOK_ATB ? CSF_EVENT_TYPES.QUICKLOOK_ATB : CSF_EVENT_TYPES.ADD_TO_CART;
    /* eslint-disable camelcase */
    const event = buildCsfEvent({
        event_type: resolvedEventType,
        creator_id: creatorId,
        u1,
        u5,
        siteId,
        product_id: productId,
        motom_product_id: motomProductId
    });
    /* eslint-enable camelcase */

    try {
        const existing = await Storage.db.getItem(CSF_JOURNEYS_KEY, true, false, false, Tables.CsfAttribution);
        const journeys = Array.isArray(existing?.journeys) ? existing.journeys : [];

        if (!journeys.length) {
            Sephora.logger.verbose('CSF dynamic commissioning: skipping ATB event, no journeys (entry event creates journeys)');

            return;
        }

        let targetIdx = -1;

        for (let i = journeys.length - 1; i >= 0; i--) {
            if (journeyContainsEntryEvent(journeys[i])) {
                targetIdx = i;

                break;
            }
        }

        if (targetIdx === -1) {
            Sephora.logger.verbose('CSF dynamic commissioning: skipping ATB event, no journey contains an entry event');

            return;
        }

        const targetJourney = journeys[targetIdx] || {};
        const targetJourneyEvents = Array.isArray(targetJourney.events) ? targetJourney.events : [];
        const updatedJourney = {
            ...targetJourney,
            events: [...targetJourneyEvents, event]
        };
        const updatedJourneys = [...journeys];
        updatedJourneys[targetIdx] = updatedJourney;

        await Storage.db.setItem(CSF_JOURNEYS_KEY, { journeys: updatedJourneys }, null, Tables.CsfAttribution);
    } catch (err) {
        Sephora.logger.error('CSF dynamic commissioning: failed to log ATB event', err);
    }
}

/**
 * Removes expired journeys from the IndexedDB csf_attribution table.
 * A journey is expired when its expires_at timestamp is less than or equal to Date.now().
 *
 * Designed to run on:
 * - CSF page load (fire-and-forget)
 * - Checkout before order submission (awaited)
 *
 * @returns {Promise<void>}
 */
export async function cleanExpiredJourneys() {
    if (!isDynamicCommissioningEnabled()) {
        return;
    }

    // SSR guard — `Storage.db` in node renders is built from `errorMethods(...)`
    // (functions that throw), so the typeof check below is not enough.
    if (Sephora.isNodeRender) {
        return;
    }

    if (typeof Storage.db?.getItem !== 'function' || typeof Storage.db?.setItem !== 'function' || typeof Storage.db?.removeItem !== 'function') {
        return;
    }

    await enqueueJourneysWrite(async () => {
        try {
            const data = await Storage.db.getItem(CSF_JOURNEYS_KEY, true, false, false, Tables.CsfAttribution);
            const journeys = Array.isArray(data?.journeys) ? data.journeys : null;

            if (!journeys?.length) {
                return;
            }

            const now = Date.now();
            const validJourneys = journeys.filter(journey => journey['expires_at'] > now);

            // Nothing expired — no write needed
            if (validJourneys.length === journeys.length) {
                return;
            }

            if (validJourneys.length === 0) {
                await Storage.db.removeItem(CSF_JOURNEYS_KEY, Tables.CsfAttribution);
                // No active journeys means any persisted u5 is stale — clear it
                // so the next entry doesn't inherit a previous journey's value.
                clearCsfDynamicCommissioningU5();
            } else {
                await Storage.db.setItem(CSF_JOURNEYS_KEY, { ...data, journeys: validJourneys }, null, Tables.CsfAttribution);
            }
        } catch (err) {
            Sephora.logger.error('CSF Dynamic Commissioning: TTL cleanup failed', err);
        }
    });
}

// ──────────────────────────────────────────────────────────────────────────────
// History-state navigation index (back/forward detection for CSF-1686)
//
// PopStateEvent does not natively expose direction. We stamp each pushed
// history entry with a monotonic `csfNavIndex`; on popstate, comparing the
// entry's index to the last seen index yields back/forward/none.
//
// The counter MUST be monotonic across the entire document session — older
// CSF entries higher up in the history stack may still be reachable via
// browser back even after the user left CSF and re-entered. We never
// decrease the counter, and persist it in `sessionStorage` so a page reload
// (which preserves history but resets module state) cannot misclassify a
// back into a still-stamped entry as forward.
// ──────────────────────────────────────────────────────────────────────────────

const CSF_NAV_INDEX_SESSION_KEY = 'csf_dc_nav_index';

let lastSeenCsfNavIndex = 0;

function readPersistedNavIndex() {
    try {
        const raw = window.sessionStorage?.getItem(CSF_NAV_INDEX_SESSION_KEY);
        const parsed = raw == null ? NaN : Number(raw);

        return Number.isFinite(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function writePersistedNavIndex(value) {
    try {
        // Persistence stores the max counter ever seen — never decreases — so
        // the monotonic guarantee from the header comment holds even when the
        // in-memory cursor moves backwards (e.g. browser back).
        const current = readPersistedNavIndex();
        const monotonic = Math.max(Number.isFinite(current) ? current : 0, value);

        window.sessionStorage?.setItem(CSF_NAV_INDEX_SESSION_KEY, String(monotonic));
    } catch {
        // sessionStorage unavailable / quota — best-effort persistence only.
    }
}

function setLastSeenCsfNavIndex(value) {
    lastSeenCsfNavIndex = value;
    writePersistedNavIndex(value);
}

/**
 * Initializes the nav-index counter for the current document. If the current
 * history entry has no `csfNavIndex` stamp, it is stamped with the next
 * monotonic index (so it cannot collide with a previously-stamped entry that
 * is still reachable higher in the history stack). The counter never
 * decreases. Safe to call multiple times.
 */
export function initCsfNavIndex() {
    if (typeof window === 'undefined') {
        return;
    }

    const currentState = window.history.state;
    const stateIndex = currentState?.csfNavIndex;
    const persistedIndex = readPersistedNavIndex();

    // Take the highest known counter — never go backwards when generating a
    // new stamp. We trust the ground-truth sources (history.state and
    // sessionStorage) over the in-memory cache so a fresh document (page
    // reload, new module instance) still recovers the correct monotonic
    // baseline for future entries.
    const baseline = Math.max(Number.isFinite(stateIndex) ? stateIndex : 0, Number.isFinite(persistedIndex) ? persistedIndex : 0);

    if (Number.isFinite(stateIndex)) {
        // When the current history entry is already stamped, restore the
        // current position from history.state rather than the persisted max.
        // This keeps back/forward direction detection aligned with the actual
        // entry the user reloaded on.
        setLastSeenCsfNavIndex(stateIndex);

        return;
    }

    // Un-stamped entry (e.g. arrived via a foreign SPA router that did not
    // call `tagPushedHistoryState`). Stamp it with `baseline + 1` to keep the
    // counter strictly monotonic and avoid colliding with any previously
    // stamped entry still reachable in the history stack.
    const nextIndex = baseline + 1;

    window.history.replaceState({ ...(currentState || {}), csfNavIndex: nextIndex }, '');
    setLastSeenCsfNavIndex(nextIndex);
}

/**
 * Stamps the just-pushed history entry with the next monotonic `csfNavIndex`.
 * Must be called immediately after `window.history.pushState(...)` inside
 * `csfNavigation.navigateTo`.
 */
export function tagPushedHistoryState() {
    if (typeof window === 'undefined') {
        return;
    }

    // Bump from the highest known counter (persisted max OR in-memory cursor)
    // so a new stamp cannot collide with an older entry still reachable in the
    // history stack — the in-memory cursor may be lower after browser back.
    const persistedIndex = readPersistedNavIndex();
    const baseline = Math.max(lastSeenCsfNavIndex, Number.isFinite(persistedIndex) ? persistedIndex : 0);
    const nextIndex = baseline + 1;
    const currentState = window.history.state;

    window.history.replaceState({ ...(currentState || {}), csfNavIndex: nextIndex }, '');
    setLastSeenCsfNavIndex(nextIndex);
}

export function isRealBrowserHistoryNavigation(event) {
    if (event?.isTrusted === true) {
        return true;
    }

    return Number.isFinite(event?.state?.csfNavIndex);
}

/**
 * Resolves the navigation direction for a popstate event by comparing the
 * popped entry's `csfNavIndex` stamp against the in-memory last-seen index.
 *
 * Side effect: when a finite stamp is found, advances/rewinds the module-level
 * last-seen cursor and persists the monotonic max to `sessionStorage` (via
 * `setLastSeenCsfNavIndex`).
 *
 * @param {PopStateEvent | { state?: { csfNavIndex?: number } }} event
 *        Popstate-shaped object; only `event.state.csfNavIndex` is read.
 * @returns {'back' | 'forward' | null}
 *        `'back'` / `'forward'` for stamped browser history navigation;
 *        `null` when the stamp is missing or equal to the last-seen index
 *        (e.g. synthetic popstate without a `state` payload).
 */
export function getNavigationTypeFromPopState(event) {
    const stateIndex = event?.state?.csfNavIndex;

    if (!Number.isFinite(stateIndex)) {
        return null;
    }

    if (stateIndex < lastSeenCsfNavIndex) {
        setLastSeenCsfNavIndex(stateIndex);

        return 'back';
    }

    if (stateIndex > lastSeenCsfNavIndex) {
        setLastSeenCsfNavIndex(stateIndex);

        return 'forward';
    }

    return null;
}

/**
 * Allowed values for `navigation_type` per the CSF dynamic commissioning event
 * schema. Anything outside this whitelist is treated as missing/invalid.
 */
const ALLOWED_NAVIGATION_TYPES = new Set([null, 'back', 'forward']);

/**
 * Required value-fields for a `page_view` event per CSF-1686 AC.
 * `om_mmc` is part of the schema (always set; `null` for non-entry events per
 * the ticket's Technical Details table) and is therefore not gated here.
 */
function getMissingRequiredPageViewFields({ creatorId, u1, siteId, navigationType }) {
    const missing = [];

    if (!creatorId) {
        missing.push('creator_id');
    }

    if (!u1) {
        missing.push('u1');
    }

    if (!siteId) {
        missing.push('siteId');
    }

    if (!ALLOWED_NAVIGATION_TYPES.has(navigationType)) {
        missing.push('navigation_type');
    }

    return missing;
}

/**
 * Appends an event to the most recent non-expired journey in the
 * `csf_attribution` IndexedDB store. No-op when there is no active journey
 * (per CSF-1686: page_view outside a journey is dropped).
 *
 * @param {object} event Pre-built CSF event.
 * @returns {Promise<void>}
 */
async function appendEventToActiveJourney(event) {
    // SSR guard — `Storage.db` in node renders is built from `errorMethods(...)`
    // (functions that throw), so the typeof check below is not enough.
    if (Sephora.isNodeRender) {
        return;
    }

    if (typeof Storage.db?.getItem !== 'function' || typeof Storage.db?.setItem !== 'function') {
        return;
    }

    await enqueueJourneysWrite(async () => {
        try {
            const existing = await Storage.db.getItem(CSF_JOURNEYS_KEY, true, false, false, Tables.CsfAttribution);
            const journeys = Array.isArray(existing?.journeys) ? existing.journeys : [];

            if (!journeys.length) {
                return;
            }

            const now = Date.now();
            let activeIdx = -1;

            for (let i = journeys.length - 1; i >= 0; i--) {
                if (journeys[i]?.['expires_at'] > now) {
                    activeIdx = i;

                    break;
                }
            }

            if (activeIdx === -1) {
                return;
            }

            const activeJourney = journeys[activeIdx];
            /* eslint-disable camelcase */
            const eventWithJourneyId = { ...event, journey_id: activeJourney.journey_id };
            /* eslint-enable camelcase */

            const existingEvents = Array.isArray(activeJourney.events) ? activeJourney.events : [];
            const updatedJourney = {
                ...activeJourney,
                events: [...existingEvents, eventWithJourneyId]
            };
            const updatedJourneys = journeys.slice();
            updatedJourneys[activeIdx] = updatedJourney;

            await Storage.db.setItem(CSF_JOURNEYS_KEY, { ...existing, journeys: updatedJourneys }, null, Tables.CsfAttribution);
        } catch (err) {
            Sephora.logger.error('CSF dynamic commissioning: failed to append event to active journey', err);
        }
    });
}

/**
 * Persists a CSF `page_view` event to the active journey when dynamic
 * commissioning is enabled. No-ops when the feature flag is off, required
 * value-fields are missing, or no active journey exists.
 *
 * @param {{
 *   destinationUrl?: string,
 *   creatorId?: string|null,
 *   u1?: string|null,
 *   siteId?: string|null,
 *   u5?: string|null,
 *   navigationType: 'back'|'forward'|null,
 *   sourceUrl?: string|null
 * }} [params]
 * @returns {Promise<void>}
 */
export async function logCsfPageViewInComponent({
    destinationUrl,
    creatorId = null,
    u1 = null,
    siteId = null,
    u5 = null,
    navigationType,
    sourceUrl = null
} = {}) {
    if (!isDynamicCommissioningEnabled()) {
        return;
    }

    if (typeof window === 'undefined') {
        return;
    }

    const missing = getMissingRequiredPageViewFields({ creatorId, u1, siteId, navigationType });

    if (missing.length) {
        Sephora.logger.verbose('CSF dynamic commissioning: skipping page_view, missing fields', missing);

        return;
    }

    /* eslint-disable camelcase */
    const event = buildCsfEvent({
        event_type: CSF_EVENT_TYPES.PAGE_VIEW,
        source_url: sourceUrl,
        destination_url: destinationUrl ?? null,
        creator_id: creatorId,
        u1,
        u5,
        siteId,
        navigation_type: navigationType ?? null,
        om_mmc: null
    });
    /* eslint-enable camelcase */

    await appendEventToActiveJourney(event);
}
